import { createAsyncThunk } from '@reduxjs/toolkit'
import { firestoreService } from '@/services/firebase/firestore.service'
import { FetchPurchasesParams, FetchPurchasesResult, Payment, PaymentConcept } from '@/types/payment.types'
import { RootState } from '@/app/store'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/services/firebase/config'

export const fetchPurchases = createAsyncThunk(
  'payments/fetchPurchases',
  async (_, { rejectWithValue }) => {
    try {
      const purchases = await firestoreService.getDocuments('purchases') as Payment[]

      // Fetch concepts subcollection for each purchase
      const purchasesWithConcepts = await Promise.all(
        purchases.map(async (purchase) => {
          const conceptsRef = collection(db, 'purchases', purchase.id, 'concepts')
          const conceptsSnapshot = await getDocs(conceptsRef)

          const concepts: PaymentConcept[] = conceptsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            amount: doc.data().amount,
            cant: 1,
          }))

          return {
            ...purchase,
            concepts,
          }
        })
      )

      return purchasesWithConcepts
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch purchases')
    }
  }
)
export const fetchFilteredPurchases = createAsyncThunk<
  FetchPurchasesResult,
  FetchPurchasesParams,
  { rejectValue: string; state: RootState }
>(
  'payments/fetchFilteredPurchases',
  async (params, { rejectWithValue, getState }) => {
    try {
      const {
        filters,
        pageSize = 10,
        direction,
        lastVisible,
        firstVisible
      } = params;

      const constraints: Array<{
        field: string;
        operator: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'in' | 'array-contains-any';
        value: any;
      }> = [];

      // ✅ Build Firestore constraints (ALL server-side filters)
      // Convert ISO strings to Date objects for Firestore comparison
      if (filters.dateRange.start) {
        constraints.push({
          field: 'createdAt',
          operator: '>=',
          value: new Date(filters.dateRange.start),
        });
      }

      if (filters.dateRange.end) {
        constraints.push({
          field: 'createdAt',
          operator: '<=',
          value: new Date(filters.dateRange.end),
        });
      }

      if (filters.status) {
        constraints.push({
          field: 'status',
          operator: '==',
          value: filters.status,
        });
      }

      if (filters.userId) {
        constraints.push({
          field: 'userId',
          operator: '==',
          value: filters.userId,
        });
      }

      // Server-side email filter (exact match or use composite index for partial)
      if (filters.userName) {
        constraints.push({
          field: 'customerEmail',
          operator: '==',
          value: filters.userName.trim(),
        });
      }

      // Server-side paymentItem filter using denormalized conceptIds array
      if (filters.paymentItem) {
        constraints.push({
          field: 'conceptIds',
          operator: 'array-contains',
          value: filters.paymentItem,
        });
      }

      const state = getState();
      const currentPage = state.payments.pagination.currentPage;
      const { cursors } = state.payments.pagination;

      // Calculate new page number
      let newPage = currentPage;
      let cursorToUse = { lastVisible, firstVisible };

      if (direction === 'next') {
        newPage = currentPage + 1;
        if (!lastVisible && cursors[currentPage]) {
          cursorToUse.lastVisible = cursors[currentPage].lastDoc;
        }
      } else if (direction === 'previous') {
        newPage = Math.max(1, currentPage - 1);
        if (!firstVisible && cursors[currentPage]) {
          cursorToUse.firstVisible = cursors[currentPage].firstDoc;
        }
      } else {
        // Reset to first page
        newPage = 1;
        cursorToUse = { lastVisible: null, firstVisible: null };
      }

      // ✅ Fetch purchases from Firestore - ALL FILTERING IS SERVER-SIDE
      const result = await firestoreService.getDocumentsWithPagination<Payment>(
        'purchases',
        constraints,
        {
          pageSize,
          direction,
          lastVisible: cursorToUse.lastVisible,
          firstVisible: cursorToUse.firstVisible,
        }
      );

      // Type guard to ensure we have pagination result
      if (!result || typeof result !== 'object' || !('data' in result)) {
        throw new Error('Invalid response from Firestore service');
      }

      // ✅ Fetch concepts for display only (no filtering needed)
      const purchasesWithConcepts = await Promise.all(
        result.data.map(async (purchase) => {
          const conceptsRef = collection(db, 'purchases', purchase.id, 'concepts');
          const conceptsSnapshot = await getDocs(conceptsRef);

          const concepts: PaymentConcept[] = conceptsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            amount: doc.data().amount,
            studentId: doc.data().studentId || undefined,
            cant: 1,
          }));

          return {
            ...purchase,
            concepts,
          };
        })
      );

      return {
        data: purchasesWithConcepts,
        lastVisible: result.lastVisible,
        firstVisible: result.firstVisible,
        hasNextPage: result.hasNextPage,
        hasPreviousPage: newPage > 1,
        currentPage: newPage,
      };
    } catch (error: any) {
      console.error('Error fetching filtered purchases:', error);
      return rejectWithValue(error.message || 'Failed to fetch purchases');
    }
  }
);

/**
 * Fetch purchases for a specific student by their identifier
 * Searches through all purchases and their concepts subcollections
 */
export const fetchStudentPurchases = createAsyncThunk<
  Payment[],
  string,
  { rejectValue: string }
>(
  'payments/fetchStudentPurchases',
  async (studentIdentifier, { rejectWithValue }) => {
    try {
      // Fetch all purchases
      const purchasesRef = collection(db, 'purchases');
      const purchasesSnapshot = await getDocs(purchasesRef);

      const studentPurchases: Payment[] = [];

      // For each purchase, check if any concept has the student's identifier
      for (const purchaseDoc of purchasesSnapshot.docs) {
        const purchaseData = purchaseDoc.data();

        // Convert Firestore Timestamp to ISO string if needed
        let createdAt = purchaseData.createdAt;
        if (createdAt?.toDate) {
          createdAt = createdAt.toDate().toISOString();
        }

        const purchase: Payment = {
          ...purchaseData,
          id: purchaseDoc.id,
          createdAt: createdAt || new Date().toISOString(),
        } as Payment;

        // Fetch concepts for this purchase
        const conceptsRef = collection(db, 'purchases', purchase.id, 'concepts');
        const conceptsQuery = query(conceptsRef, where('studentId', '==', studentIdentifier));
        const conceptsSnapshot = await getDocs(conceptsQuery);

        // If this purchase has concepts for this student
        if (!conceptsSnapshot.empty) {
          const concepts: PaymentConcept[] = conceptsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            amount: doc.data().amount,
            studentId: doc.data().studentId || undefined,
            cant: 1,
          }));

          studentPurchases.push({
            ...purchase,
            concepts,
          });
        }
      }

      // Sort purchases by date (newest first)
      studentPurchases.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });

      return studentPurchases;
    } catch (error: any) {
      console.error('Error fetching student purchases:', error);
      return rejectWithValue(error.message || 'Failed to fetch student purchases');
    }
  }
);

/**
 * Fetch purchases by user identifier
 * Fetches all purchases where userId matches the provided user identifier
 */
export const fetchPurchasesByUserIdentifier = createAsyncThunk<
  Payment[],
  string,
  { rejectValue: string }
>(
  'payments/fetchPurchasesByUserIdentifier',
  async (userIdentifier, { rejectWithValue }) => {
    try {
      // Query purchases where userId matches the user identifier
      const purchasesRef = collection(db, 'purchases');
      const purchasesQuery = query(purchasesRef, where('userId', '==', userIdentifier));
      const purchasesSnapshot = await getDocs(purchasesQuery);

      const userPurchases: Payment[] = [];

      // For each purchase, fetch its concepts
      for (const purchaseDoc of purchasesSnapshot.docs) {
        const purchaseData = purchaseDoc.data();

        // Convert Firestore Timestamp to ISO string if needed
        let createdAt = purchaseData.createdAt;
        if (createdAt?.toDate) {
          createdAt = createdAt.toDate().toISOString();
        }

        const purchase: Payment = {
          ...purchaseData,
          id: purchaseDoc.id,
          createdAt: createdAt || new Date().toISOString(),
        } as Payment;

        // Fetch all concepts for this purchase
        const conceptsRef = collection(db, 'purchases', purchase.id, 'concepts');
        const conceptsSnapshot = await getDocs(conceptsRef);

        const concepts: PaymentConcept[] = conceptsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          amount: doc.data().amount,
          studentId: doc.data().studentId || undefined,
          cant: 1,
        }));

        userPurchases.push({
          ...purchase,
          concepts,
        });
      }

      // Sort purchases by date (newest first)
      userPurchases.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });

      return userPurchases;
    } catch (error: any) {
      console.error('Error fetching purchases by user identifier:', error);
      return rejectWithValue(error.message || 'Failed to fetch user purchases');
    }
  }
);
